/* ==========================================================================
   Disco Tribe SMS popup — assets/disco-sms-popup.js
   - Opens/closes the artwork popup (timing, frequency, dismissal rules)
   - Validates + normalizes phone numbers to E.164 (+13145551234)
   - Subscribes with explicit SMS marketing consent via Klaviyo's documented
     client endpoint: POST https://a.klaviyo.com/client/subscriptions
     (public API key only — https://developers.klaviyo.com/en/reference/create_client_subscription)
   - Keeps the field visible above phone keyboards (visualViewport)
   - Optional ALIGNMENT DEV MODE: outlines + nudge panel (off in production)
   - Does NOT load klaviyo.js (your Klaviyo app embed already does that)
   ========================================================================== */
(function () {
  'use strict';
  if (window.DiscoTribePopup) return; // guard against double-loading

  var KLAVIYO_URL = 'https://a.klaviyo.com/client/subscriptions';
  var KEY = {
    subscribed: 'dtp_subscribed',
    dismissedUntil: 'dtp_dismissed_until',
    shown: 'dtp_shown_session',
    pageviews: 'dtp_pageviews'
  };
  var instances = {};

  /* ---------- Email-popup events (Omnisend + Klaviyo) ----------
     Both apps announce popup activity on window. We translate them into one
     shape: { src, type: open|close|submit|stepSubmit, formId, embedded, email }.
     • Omnisend: "omnisendForms" (detail.type or detail.eventName = view |
       interaction | submit | close | stepView | stepInteraction | stepSubmit;
       detail.form.id / .displayType; detail.formValues.emailField)
       Docs: api-docs.omnisend.com → "Omnisend forms events"
     • Klaviyo: "klaviyoForms" (detail.type = open | close | submit | stepSubmit |
       embedOpen; detail.formId; detail.metaData.email)
       Docs: developers.klaviyo.com → "Track Klaviyo form activity using JavaScript"
     Recorded from the moment this file loads so none are missed. */
  var klEvents = [];
  var klListeners = [];
  function emitFormEvent(ev) {
    if (!ev || !ev.type) return;
    klEvents.push(ev);
    klListeners.slice().forEach(function (fn) { try { fn(ev); } catch (err) {} });
  }
  window.addEventListener('omnisendForms', function (e) {
    var d = (e && e.detail) || {};
    var t = String(d.type || d.eventName || '');
    var map = { view: 'open', submit: 'submit', stepSubmit: 'stepSubmit', close: 'close' };
    if (!map[t]) return; // interaction / stepView / stepInteraction aren't needed
    var form = d.form || {};
    var vals = d.formValues || {};
    emitFormEvent({
      src: 'omnisend',
      type: map[t],
      formId: form.id || form.ID || '',
      embedded: /embed/i.test(String(form.displayType || '')),
      email: vals.emailField || vals.email || ''
    });
  });
  window.addEventListener('klaviyoForms', function (e) {
    var d = (e && e.detail) || {};
    var md = d.metaData || {};
    emitFormEvent({
      src: 'klaviyo',
      type: d.type === 'embedOpen' ? 'open' : d.type,
      formId: d.formId || '',
      embedded: d.type === 'embedOpen',
      email: md.email || md.$email || ''
    });
  });

  /* Is this element really on screen? Themes keep hidden drawers/modals in the
     page (e.g. Dawn's cart drawer is always marked aria-modal="true"), so the
     attribute alone means nothing. */
  function isShowing(el) {
    if (!el || !el.getClientRects().length) return false;
    var cs = getComputedStyle(el);
    if (cs.visibility === 'hidden' || cs.display === 'none') return false;
    for (var n = el; n && n !== document.body && n.nodeType === 1; n = n.parentElement) {
      var s = getComputedStyle(n);
      if (s.display === 'none' || parseFloat(s.opacity) < 0.05) return false;
    }
    var r = el.getBoundingClientRect();
    var vw = window.innerWidth, vh = window.innerHeight;
    var w = Math.min(r.right, vw) - Math.max(r.left, 0);
    var h = Math.min(r.bottom, vh) - Math.max(r.top, 0);
    return w > 60 && h > 60;
  }
  // Another popup / modal currently covering the page (ours excluded)?
  function otherModalShowing() {
    var list = document.querySelectorAll('dialog[open], [aria-modal="true"]');
    for (var i = 0; i < list.length; i++) {
      if (list[i].closest('.dtp')) continue;
      if (isShowing(list[i])) return true;
    }
    return false;
  }

  /* ---------- small helpers ---------- */
  function store(type) {
    try {
      var s = window[type];
      s.setItem('__dtp_t', '1');
      s.removeItem('__dtp_t');
      return s;
    } catch (e) {
      return null; // private mode / blocked storage: popup still works, just can't remember
    }
  }
  var local = store('localStorage');
  var session = store('sessionStorage');
  function get(s, k) { try { return s ? s.getItem(k) : null; } catch (e) { return null; } }
  function set(s, k, v) { try { if (s) s.setItem(k, v); } catch (e) {} }
  function del(s, k) { try { if (s) s.removeItem(k); } catch (e) {} }
  function param(name) {
    try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
  }
  function isTouch() {
    return !!(window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches);
  }
  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ---------- phone validation ---------- */
  // Returns { ok: true, e164 } or { ok: false, reason: 'empty'|'invalid'|'country' }
  function normalizePhone(raw, allowIntl) {
    var str = String(raw || '').trim();
    if (!str) return { ok: false, reason: 'empty' };
    if (/[a-z]/i.test(str.replace(/ext\.?|x/gi, ''))) return { ok: false, reason: 'invalid' };
    var intl = str.charAt(0) === '+' || str.indexOf('00') === 0;
    var digits = str.replace(/\D/g, '');
    if (str.indexOf('00') === 0) digits = digits.slice(2);

    var nanp;
    if (!intl) {
      if (digits.length === 10) nanp = digits;
      else if (digits.length === 11 && digits.charAt(0) === '1') nanp = digits.slice(1);
      else return { ok: false, reason: 'invalid' };
    } else if (digits.charAt(0) === '1') {
      if (digits.length !== 11) return { ok: false, reason: 'invalid' };
      nanp = digits.slice(1);
    } else {
      if (!allowIntl) return { ok: false, reason: 'country' };
      if (digits.length < 8 || digits.length > 15) return { ok: false, reason: 'invalid' };
      return { ok: true, e164: '+' + digits };
    }
    // US/Canada: area code and exchange can't start with 0/1; no N11 area codes
    if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(nanp)) return { ok: false, reason: 'invalid' };
    if (/^[2-9]11/.test(nanp)) return { ok: false, reason: 'invalid' };
    return { ok: true, e164: '+1' + nanp };
  }

  function formatUS(value) {
    if (/^\s*\+/.test(value)) return value; // leave international entries alone
    var d = value.replace(/\D/g, '');
    if (d.length === 11 && d.charAt(0) === '1') d = d.slice(1);
    if (d.length > 10) return value;
    if (d.length < 4) return d;
    if (d.length < 7) return '(' + d.slice(0, 3) + ') ' + d.slice(3);
    return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6);
  }

  /* ---------- Shopify customer privacy (optional) ---------- */
  function marketingAllowed(cb) {
    function check() {
      var api = window.Shopify && window.Shopify.customerPrivacy;
      if (api && typeof api.marketingAllowed === 'function') return cb(!!api.marketingAllowed());
      cb(true);
    }
    if (window.Shopify && window.Shopify.customerPrivacy) return check();
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
        if (err) return cb(true);
        check();
      });
    } else {
      cb(true);
    }
  }

  /* ======================================================================
     Popup instance
     ====================================================================== */
  function Popup(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id');
    var cfgEl = root.querySelector('[data-dtp-config]');
    try { this.cfg = JSON.parse(cfgEl.textContent); } catch (e) { this.cfg = {}; }
    this.dialog = root.querySelector('.dtp__dialog');
    this.stage = root.querySelector('[data-dtp-stage]');
    this.card = root.querySelector('.dtp__card');
    this.form = root.querySelector('[data-dtp-form]');
    this.input = root.querySelector('[data-dtp-input]');
    this.hp = root.querySelector('[data-dtp-hp]');
    this.submitBtn = root.querySelector('[data-dtp-submit]');
    this.msg = root.querySelector('[data-dtp-msg]');
    this.success = root.querySelector('[data-dtp-success]');
    this.disclosure = root.querySelector('[data-dtp-disclosure]');
    this.busy = false;
    this.done = false;
    this.isOpen = false;
    this.timers = [];
    this.cleanups = [];

    // Live on <body> so theme wrappers (transforms, overflow) can't clip a fixed popup.
    if (root.parentNode !== document.body) document.body.appendChild(root);

    this.bind();
    this.placeDisclosure();
    this.watchDisclosureSize();
    this.watchStageSize();
    this.watchKeyboard();
    this.decide();
  }

  Popup.prototype.on = function (el, ev, fn, opts) {
    if (!el) return;
    el.addEventListener(ev, fn, opts);
    this.cleanups.push(function () { el.removeEventListener(ev, fn, opts); });
  };
  Popup.prototype.later = function (fn, ms) { this.timers.push(setTimeout(fn, ms)); };

  Popup.prototype.bind = function () {
    var self = this;
    this.root.querySelectorAll('[data-dtp-close]').forEach(function (b) {
      self.on(b, 'click', function () { self.close('dismiss'); });
    });
    this.on(this.root.querySelector('[data-dtp-backdrop]'), 'click', function () {
      if (self.cfg.closeOnBackdrop) self.close('dismiss');
    });
    this.on(this.root, 'keydown', function (e) { self.onKeydown(e); });
    this.on(this.form, 'submit', function (e) { e.preventDefault(); self.submit(); });
    this.on(this.input, 'input', function () {
      self.clearError();
      var el = self.input;
      if (el.selectionStart === el.value.length) {
        var f = formatUS(el.value);
        if (f !== el.value) el.value = f;
      }
    });
    this.on(this.input, 'blur', function () { self.input.value = formatUS(self.input.value); });

    if (window.matchMedia) {
      var mq = window.matchMedia('(min-width: 600px)');
      var handler = function () { self.placeDisclosure(); };
      if (mq.addEventListener) {
        mq.addEventListener('change', handler);
        this.cleanups.push(function () { mq.removeEventListener('change', handler); });
      }
    }
  };

  /* ---------- disclosure placement + reserved height ---------- */
  Popup.prototype.placeDisclosure = function () {
    var mode = (this.root.className.match(/dtp--disc-(\S+)/) || [])[1] || 'below';
    var wide = window.matchMedia ? window.matchMedia('(min-width: 600px)').matches : true;
    var overlay = mode === 'overlay' || (mode === 'overlay_desktop' && wide);
    if (overlay && this.disclosure.parentNode !== this.stage) {
      this.disclosure.classList.add('dtp__disc-overlay');
      this.stage.insertBefore(this.disclosure, this.stage.querySelector('.dtp__close'));
    } else if (!overlay && this.disclosure.parentNode !== this.card) {
      this.disclosure.classList.remove('dtp__disc-overlay');
      this.card.appendChild(this.disclosure);
    }
    this.measureDisclosure();
    this.fitDisclosure();
  };

  // Tell CSS how much height the disclosure card takes, so the artwork shrinks
  // just enough for art + disclosure to fit on short screens (no clipping).
  Popup.prototype.measureDisclosure = function () {
    var below = this.disclosure.parentNode === this.card && getComputedStyle(this.card).display !== 'flex';
    var h = below ? this.disclosure.getBoundingClientRect().height + 14 : 0;
    var prev = this._discSpace || 0;
    if (Math.abs(h - prev) > 1) {
      this._discSpace = h;
      this.root.style.setProperty('--dtp-disc-space', Math.ceil(h) + 'px');
    }
  };
  Popup.prototype.watchDisclosureSize = function () {
    var self = this;
    if (typeof ResizeObserver === 'undefined') return;
    var raf = 0;
    var ro = new ResizeObserver(function () {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () { self.measureDisclosure(); });
    });
    ro.observe(this.disclosure);
    this.cleanups.push(function () { ro.disconnect(); });
  };

  /* ---------- text scale: keep typed text proportional to the artwork ----------
     --field-k = (artwork text size in real px, but at least --field-min-px) ÷ 16px,
     capped at 1. The field
     keeps a real ≥16px font (no iPhone zoom) and is visually scaled by k. */
  Popup.prototype.updateFieldScale = function () {
    var w = this.stage.getBoundingClientRect().width;
    if (!w) return;
    var cs = getComputedStyle(this.root);
    var refW = parseFloat(cs.getPropertyValue('--dtp-ref-w')) || 1310;
    var fs = parseFloat(cs.getPropertyValue('--field-fs')) || 36;
    var minPx = parseFloat(cs.getPropertyValue('--field-min-px')) || 12;   // readability floor on tiny phones
    var k = Math.min(1, Math.max(fs * w / refW, minPx) / 16);
    if (Math.abs(k - (this._k || 0)) > 0.0005) {
      this._k = k;
      this.root.style.setProperty('--field-k', k.toFixed(4));
    }
    if (this.devOn) this.devReport(); // keep the dev readout's scale current
  };
  /* Disclosure on the ball: largest font (up to --disc-max artwork px) that fits the label. */
  Popup.prototype.fitDisclosure = function () {
    var el = this.disclosure;
    if (!el || !el.classList.contains('dtp__disc-overlay')) return;
    var w = this.stage.getBoundingClientRect().width;
    if (!w || !el.clientHeight) return;
    var cs = getComputedStyle(this.root);
    var refW = parseFloat(cs.getPropertyValue('--dtp-ref-w')) || 1310;
    var maxArt = parseFloat(cs.getPropertyValue('--disc-max')) || 22;
    var hi = Math.min(13, maxArt * w / refW);
    var lo = 4;
    var inner = el.firstElementChild || el;
    var fits = function (px) {
      el.style.setProperty('--disc-fs', px + 'px');
      var pad = parseFloat(getComputedStyle(el).paddingTop) + parseFloat(getComputedStyle(el).paddingBottom);
      return inner.scrollHeight <= el.clientHeight - pad + 0.5 && inner.scrollWidth <= el.clientWidth + 0.5;
    };
    if (fits(hi)) return;
    for (var i = 0; i < 14; i++) { var mid = (lo + hi) / 2; if (fits(mid)) lo = mid; else hi = mid; }
    el.style.setProperty('--disc-fs', (Math.floor(lo * 10) / 10) + 'px');
  };

  Popup.prototype.watchStageSize = function () {
    var self = this;
    this.updateFieldScale();
    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(function () { self.updateFieldScale(); self.fitDisclosure(); });
      ro.observe(this.stage);
      this.cleanups.push(function () { ro.disconnect(); });
    } else {
      this.on(window, 'resize', function () { self.updateFieldScale(); self.fitDisclosure(); });
    }
    // fonts can load late and change text width
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { self.fitDisclosure(); });
  };

  /* ---------- phone keyboards ----------
     iOS Safari doesn't shrink fixed elements when the keyboard opens, so the
     field can end up hidden. We pin the overlay to the *visual* viewport and
     scroll the field into the visible area. The artwork itself never resizes. */
  Popup.prototype.watchKeyboard = function () {
    var self = this;
    var vv = window.visualViewport;
    if (!vv) return;
    var update = function () {
      if (!self.isOpen) return;
      var layoutH = document.documentElement.clientHeight || window.innerHeight;
      var kb = document.activeElement === self.input && layoutH - vv.height > 120;
      self.root.classList.toggle('is-kb', kb);
      if (kb) {
        self.root.style.top = vv.offsetTop + 'px';
        self.root.style.height = vv.height + 'px';
        self.root.style.bottom = 'auto';
        var r = self.input.getBoundingClientRect();
        var rr = self.root.getBoundingClientRect();
        var want = r.top - rr.top + self.root.scrollTop - vv.height * 0.38;
        self.root.scrollTop = Math.max(0, want);
      } else {
        self.root.style.top = '';
        self.root.style.height = '';
        self.root.style.bottom = '';
      }
    };
    this.on(vv, 'resize', update);
    this.on(vv, 'scroll', update);
    this.on(this.input, 'focus', function () { self.later(update, 60); self.later(update, 350); });
    this.on(this.input, 'blur', function () { self.later(update, 60); });
  };

  /* ---------- should we show, and when? ---------- */
  Popup.prototype.decide = function () {
    var c = this.cfg;
    var self = this;

    if (param('dtp_reset') === '1') {
      [KEY.subscribed, KEY.dismissedUntil].forEach(function (k) { del(local, k); });
      [KEY.shown, KEY.pageviews].forEach(function (k) { del(session, k); });
    }

    // Dev mode ONLY when the setting is on AND (theme editor OR ?dtp_dev=1).
    var dev = !!c.devMode && (c.designMode || param('dtp_dev') === '1');
    if (dev) this.enableDev();

    if ((c.designMode && c.previewInEditor) || param('dtp_preview') === '1' || dev) {
      this.open(false);
      return;
    }
    if (c.designMode) return;
    if (c.testMode) return; // test mode never shows to real shoppers
    if (!c.publicKey || !c.listId) {
      console.warn('[Disco popup] Not showing: add your Klaviyo Public API key and List ID in the section settings.');
      return;
    }
    if (!c.hasArt) return;

    var pv = parseInt(get(session, KEY.pageviews) || '0', 10) + 1;
    set(session, KEY.pageviews, String(pv));

    if (get(local, KEY.subscribed)) return;
    var until = parseInt(get(local, KEY.dismissedUntil) || '0', 10);
    if (until && Date.now() < until) return;
    if (c.oncePerSession && get(session, KEY.shown)) return;
    if (pv < (c.minPageviews || 1)) return;

    var path = window.location.pathname.toLowerCase();
    var stripped = path.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/)/, ''); // locale prefixes like /en-ca
    var excluded = String(c.excludePaths || '').split(/\r?\n/).some(function (line) {
      var p = line.trim().toLowerCase().replace(/\/$/, '');
      if (!p) return false;
      return [path, stripped].some(function (x) { return x === p || x.indexOf(p + '/') === 0; });
    });
    if (excluded) return;
    if (c.skipSmsTraffic && (param('utm_medium') || '').toLowerCase() === 'sms') return;

    var arm = function () { self.arm(); };
    if (c.respectPrivacy) {
      marketingAllowed(function (ok) {
        if (ok) return arm();
        self.on(document, 'visitorConsentCollected', function (e) {
          if (e && e.detail && e.detail.marketingAllowed) arm();
        });
      });
    } else {
      arm();
    }
  };

  Popup.prototype.arm = function () {
    if (this.armed) return;
    this.armed = true;
    var self = this;
    var c = this.cfg;
    if (c.sequence === 'after_email') { this.armSequence(); return; }
    var fire = function () {
      if (!self.isOpen && !self.firedOnce) { self.firedOnce = true; self.openWhenClear(); }
    };
    var trig = c.trigger || 'time';
    var delayMs = Math.max(0, (c.delay || 0) * 1000);

    if (trig === 'time' || trig === 'time_or_exit' || (trig === 'exit' && isTouch())) this.later(fire, delayMs);
    if ((trig === 'exit' || trig === 'time_or_exit') && !isTouch()) {
      this.on(document, 'mouseout', function (e) { if (!e.relatedTarget && e.clientY <= 0) fire(); });
    }
    if (trig === 'scroll') {
      this.on(window, 'scroll', function () {
        var h = document.documentElement;
        var max = (h.scrollHeight - window.innerHeight) || 1;
        if ((window.scrollY / max) * 100 >= (c.scrollPercent || 40)) fire();
      }, { passive: true });
    }
  };

  /* ---------- "Second popup" mode: show after the email popup ----------
     - Email popup opens  → wait for it.
     - Shopper closes it (X, or after signing up) → SMS popup after the gap.
     - Shopper signs up but leaves it open → SMS popup after submitWait seconds
       (still waits politely if the email popup is covering the screen).
     - Email popup never appears (already signed up / dismissed before, or it
       doesn't run on this page) → SMS popup after fallback seconds.
     - Works with any email popup app for the "wait until it's gone" part;
       the open/close/submit signals come from Klaviyo forms. */
  Popup.prototype.armSequence = function () {
    var self = this;
    var c = this.cfg;
    var st = { seen: false, done: false };
    var tFallback = null, tSubmit = null, tClose = null;
    var sec = function (v, d) { var n = parseFloat(v); return (isNaN(n) ? d : n) * 1000; };
    var wanted = String(c.emailFormId || '').trim();
    var matches = function (d) {
      if (!d || !d.type) return false;
      if (d.embedded) return false;                       // inline/footer forms aren't popups
      return !wanted || String(d.formId) === wanted;
    };
    var go = function (why) {
      if (st.done) return;
      st.done = true;
      [tFallback, tSubmit, tClose].forEach(function (t) { if (t) clearTimeout(t); });
      self.seqReason = why;
      self.openWhenClear();
    };
    var handle = function (d) {
      if (st.done || !matches(d)) return;
      if (d.type === 'open') {
        st.seen = true;
        if (tFallback) { clearTimeout(tFallback); tFallback = null; }
      } else if (d.type === 'submit' || d.type === 'stepSubmit') {
        st.seen = true;
        if (tFallback) { clearTimeout(tFallback); tFallback = null; }
        var em = d.email;
        if (em && /.+@.+\..+/.test(String(em))) self.knownEmail = String(em).trim();
        if (d.type === 'submit' && !tSubmit) tSubmit = setTimeout(function () { go('email-submitted'); }, sec(c.submitWait, 30));
      } else if (d.type === 'close') {
        st.seen = true;
        if (tFallback) { clearTimeout(tFallback); tFallback = null; }
        if (tClose) clearTimeout(tClose);
        tClose = setTimeout(function () { go('email-closed'); }, sec(c.gap, 2));
      }
    };
    klEvents.forEach(handle);           // anything that happened before we armed
    klListeners.push(handle);
    this.cleanups.push(function () {
      var i = klListeners.indexOf(handle);
      if (i > -1) klListeners.splice(i, 1);
      [tFallback, tSubmit, tClose].forEach(function (t) { if (t) clearTimeout(t); });
    });
    if (!st.seen) tFallback = setTimeout(function () { if (!st.seen) go('no-email-popup'); }, sec(c.fallback, 20));
  };

  // Open as soon as no other popup/drawer is covering the screen (never stacks).
  Popup.prototype.openWhenClear = function () {
    var self = this;
    var c = this.cfg;
    var started = Date.now();
    var clearSince = null;
    var gapMs = c.sequence === 'after_email' ? Math.max(0, (parseFloat(c.gap) || 0) * 1000) : 0;
    var tick = function () {
      if (self.isOpen) return;
      if (otherModalShowing() && Date.now() - started < 120000) {
        clearSince = null;
        self.later(tick, 600);
        return;
      }
      if (clearSince === null) clearSince = Date.now();
      // if we had to wait for another popup, leave a short breath after it closes
      var waited = Date.now() - started > 700;
      if (waited && Date.now() - clearSince < gapMs) { self.later(tick, 300); return; }
      self.open(true);
    };
    tick();
  };

  /* ---------- open / close ---------- */
  Popup.prototype.open = function (countIt) {
    if (this.isOpen) return;
    var self = this;
    this.isOpen = true;
    this.lastFocus = document.activeElement;
    this.root.hidden = false;
    document.documentElement.classList.add('dtp-lock');
    if (countIt) set(session, KEY.shown, '1');
    this.measureDisclosure();
    this.updateFieldScale();
    this.fitDisclosure();
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        self.root.classList.add('is-open');
        if (self.devOn) self.devReport();
      });
    });
    // On phones, focusing the field would pop the keyboard immediately — focus the dialog instead.
    this.later(function () {
      if (self.done) self.success.focus();
      else if (isTouch() || self.devOn) self.dialog.focus();
      else self.input.focus({ preventScroll: true });
    }, 60);
    document.dispatchEvent(new CustomEvent('dtp:open'));
  };

  Popup.prototype.close = function (reason) {
    if (!this.isOpen) return;
    var self = this;
    this.isOpen = false;
    this.root.classList.remove('is-open', 'is-kb');
    this.root.style.top = this.root.style.height = this.root.style.bottom = '';
    document.documentElement.classList.remove('dtp-lock');
    if (reason === 'dismiss' && !this.done && !this.cfg.designMode && !this.devOn) {
      var days = this.cfg.dismissDays || 0;
      if (days > 0) set(local, KEY.dismissedUntil, String(Date.now() + days * 864e5));
    }
    this.later(function () { if (!self.isOpen) self.root.hidden = true; }, reducedMotion() ? 0 : 300);
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      try { this.lastFocus.focus({ preventScroll: true }); } catch (e) {}
    }
    document.dispatchEvent(new CustomEvent('dtp:close', { detail: { reason: reason } }));
  };

  Popup.prototype.onKeydown = function (e) {
    if (!this.isOpen) return;
    if (e.key === 'Escape') { e.preventDefault(); this.close('dismiss'); return; }
    if (e.key !== 'Tab') return;
    var nodes = Array.prototype.filter.call(
      this.dialog.querySelectorAll('a[href], button, input:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'),
      function (el) {
        return !el.disabled && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
      }
    );
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  };

  /* ---------- messages / states ---------- */
  Popup.prototype.showError = function (text) {
    this.msg.textContent = text;
    this.input.setAttribute('aria-invalid', 'true');
  };
  Popup.prototype.clearError = function () {
    if (!this.msg.textContent) return;
    this.msg.textContent = '';
    this.input.removeAttribute('aria-invalid');
  };
  Popup.prototype.setBusy = function (on) {
    this.busy = on;
    this.root.classList.toggle('is-busy', on);
    this.submitBtn.setAttribute('aria-disabled', on ? 'true' : 'false');
    this.input.readOnly = on;
  };
  Popup.prototype.showSuccess = function (isTest) {
    var self = this;
    this.done = true;
    if (document.activeElement === this.input) this.input.blur(); // drop the keyboard
    this.root.classList.add('is-success');
    this.success.hidden = false;
    var note = this.root.querySelector('[data-dtp-testnote]');
    if (note) note.hidden = !isTest;
    this.success.focus();
    if (!isTest) {
      set(local, KEY.subscribed, String(Date.now()));
      del(local, KEY.dismissedUntil);
    }
    // For your own analytics. Never contains the phone number.
    document.dispatchEvent(new CustomEvent('dtp:subscribed', { detail: { test: !!isTest } }));
    if (this.cfg.autoClose > 0) this.later(function () { self.close('success'); }, this.cfg.autoClose * 1000);
  };

  /* ---------- submit ---------- */
  Popup.prototype.submit = function () {
    var c = this.cfg;
    var m = c.messages || {};
    var self = this;
    if (this.busy || this.done) return; // duplicate-submit guard

    if (this.hp && this.hp.value) { this.showSuccess(true); return; } // bot honeypot

    var res = normalizePhone(this.input.value, c.allowIntl);
    if (!res.ok) {
      this.showError(m[res.reason] || m.invalid);
      this.input.focus();
      return;
    }
    this.clearError();

    if (c.testMode) {
      this.setBusy(true);
      this.later(function () {
        self.setBusy(false);
        console.info('[Disco popup] TEST MODE — would subscribe', res.e164, 'to list', c.listId || '(no list ID yet)');
        self.showSuccess(true);
      }, 900);
      return;
    }

    if (!c.publicKey || !c.listId) {
      this.showError(m.notConfigured);
      console.error('[Disco popup] Missing Klaviyo Public API key or List ID in section settings.');
      return;
    }

    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: c.source || 'Disco Tribe SMS popup',
          profile: {
            data: {
              type: 'profile',
              attributes: (function () {
                var a = {
                  phone_number: res.e164,
                  // SMS marketing consent only, and only because the shopper pressed
                  // submit next to the disclosure. Email/transactional are untouched.
                  subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } }
                };
                // If they just signed up in your email popup, attach that email so the
                // phone lands on the SAME Klaviyo profile. Email is NOT in the
                // subscriptions object, so this does not change email consent.
                if (self.knownEmail && c.linkEmail !== false) a.email = self.knownEmail;
                return a;
              })()
            }
          }
        },
        relationships: { list: { data: { type: 'list', id: c.listId } } }
      }
    };

    this.setBusy(true);
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timeout = setTimeout(function () { if (ctrl) ctrl.abort(); }, 15000);

    fetch(KLAVIYO_URL + '?company_id=' + encodeURIComponent(c.publicKey), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
        revision: c.revision || '2026-10-15'
      },
      body: JSON.stringify(body),
      signal: ctrl ? ctrl.signal : undefined
    })
      .then(function (r) {
        clearTimeout(timeout);
        if (r.status === 202 || r.ok) { self.setBusy(false); self.showSuccess(false); return; }
        return r.text().then(function (txt) {
          self.setBusy(false);
          var detail = '';
          try {
            detail = (JSON.parse(txt).errors || []).map(function (er) { return er.detail || er.title; }).join(' | ');
          } catch (e) { detail = txt; }
          console.error('[Disco popup] Klaviyo returned', r.status, detail);
          if (r.status === 429) self.showError(m.rate);
          else if (r.status >= 500) self.showError(m.network);
          else if (/phone/i.test(detail)) self.showError(m.invalid);
          else self.showError(m.server);
        });
      })
      .catch(function (err) {
        clearTimeout(timeout);
        self.setBusy(false);
        console.error('[Disco popup] Network error', err);
        self.showError(m.network);
      });
  };

  /* ======================================================================
     ALIGNMENT DEV MODE
     Outlines: white dashed = tap area, cyan = real phone field (tinted part =
     where text/caret can go), yellow = printed-label cover, magenta = real
     button, orange = loading label, green = close button.
     Panel: pick a target, nudge with the buttons or the keyboard
       arrows = move 1px · Shift+arrows = 10px · Alt/Option+arrows = resize
     (all in ORIGINAL ARTWORK pixels). "Copy values" gives you the numbers to
     type into the theme settings. Nothing here is saved automatically.
     ====================================================================== */
  var DEV_TARGETS = {
    field:  { label: 'Phone field', color: '#00d9ff', vars: ['field-x', 'field-y', 'field-w', 'field-h'], settings: ['Field X', 'Field Y', 'Field width', 'Field height'] },
    text:   { label: 'Text padding', color: '#7fe9ff', vars: ['field-pad-l', 'field-pad-r'], settings: ['Text starts…left edge', 'Text stops…right edge'] },
    btn:    { label: 'Button', color: '#ff2bd6', vars: ['btn-x', 'btn-y', 'btn-w', 'btn-h'], settings: ['Button X', 'Button Y', 'Button width', 'Button height'] },
    box:    { label: 'Tap area', color: '#ffffff', vars: ['box-x', 'box-y', 'box-w', 'box-h'], settings: ['Cream box X', 'Cream box Y', 'Cream box width', 'Cream box height'] },
    cover:  { label: 'Label cover', color: '#ffd400', vars: ['cover-x', 'cover-y', 'cover-w', 'cover-h'], settings: ['Printed-label cover X', '…Y', '…width', '…height'] },
    bcover: { label: 'Loading label', color: '#ff8a00', vars: ['bcover-x', 'bcover-y', 'bcover-w', 'bcover-h'], settings: ['Loading-label X', '…Y', '…width', '…height'] }
  };

  Popup.prototype.dv = function (name) {
    return parseFloat(getComputedStyle(this.root).getPropertyValue('--' + name)) || 0;
  };
  Popup.prototype.setDv = function (name, val) {
    this.root.style.setProperty('--' + name, String(Math.round(val * 10) / 10));
  };

  Popup.prototype.enableDev = function () {
    var self = this;
    this.devOn = true;
    this.devSel = 'field';
    this.devStep = 1;
    this.root.classList.add('dtp--dev', 'dtp--dev-sel-field');
    // remember the theme values so "Reset" can restore them
    this.devOriginal = {};
    Object.keys(DEV_TARGETS).forEach(function (k) {
      DEV_TARGETS[k].vars.forEach(function (v) { self.devOriginal[v] = self.dv(v); });
    });

    var p = document.createElement('div');
    p.className = 'dtp__dev';
    p.setAttribute('role', 'region');
    p.setAttribute('aria-label', 'Popup alignment tools');
    var targetBtns = Object.keys(DEV_TARGETS).map(function (k) {
      var t = DEV_TARGETS[k];
      return '<button type="button" data-t="' + k + '"><span class="dtp__dev-key" style="background:' + t.color + '"></span>' + t.label + '</button>';
    }).join('');
    p.innerHTML =
      '<div class="dtp__dev-row" style="margin-top:0;justify-content:space-between">' +
        '<strong>Alignment dev mode</strong><button type="button" data-a="min">Hide</button></div>' +
      '<div class="dtp__dev-body">' +
        '<div class="dtp__dev-row">' + targetBtns + '</div>' +
        '<div class="dtp__dev-row">' +
          '<button type="button" data-a="left" aria-label="Move left">←</button>' +
          '<button type="button" data-a="right" aria-label="Move right">→</button>' +
          '<button type="button" data-a="up" aria-label="Move up">↑</button>' +
          '<button type="button" data-a="down" aria-label="Move down">↓</button>' +
          '<button type="button" data-a="w-">W−</button><button type="button" data-a="w+">W+</button>' +
          '<button type="button" data-a="h-">H−</button><button type="button" data-a="h+">H+</button>' +
          '<button type="button" data-a="step" aria-pressed="false">Step ×10</button>' +
        '</div>' +
        '<div class="dtp__dev-row">' +
          '<button type="button" data-a="peek" aria-pressed="false">Peek at art</button>' +
          '<button type="button" data-a="draw" aria-pressed="false">Draw box</button>' +
          '<button type="button" data-a="copy">Copy values</button>' +
          '<button type="button" data-a="reset">Reset</button>' +
        '</div>' +
        '<div class="dtp__dev-out" data-dev-out></div>' +
      '</div>';
    document.body.appendChild(p);
    this.devPanel = p;
    this.cleanups.push(function () { p.remove(); });

    p.addEventListener('click', function (e) {
      var b = e.target.closest('button');
      if (!b) return;
      if (b.hasAttribute('data-t')) return self.devSelect(b.getAttribute('data-t'));
      var a = b.getAttribute('data-a');
      var step = self.devStep;
      if (a === 'min') { p.classList.toggle('is-min'); b.textContent = p.classList.contains('is-min') ? 'Show' : 'Hide'; return; }
      if (a === 'step') { self.devStep = self.devStep === 1 ? 10 : 1; b.setAttribute('aria-pressed', self.devStep === 10 ? 'true' : 'false'); return; }
      if (a === 'peek') { var on = self.root.classList.toggle('dtp--dev-peek'); b.setAttribute('aria-pressed', on ? 'true' : 'false'); return; }
      if (a === 'draw') { return self.devToggleDraw(b); }
      if (a === 'copy') { return self.devCopy(b); }
      if (a === 'reset') {
        Object.keys(self.devOriginal).forEach(function (v) { self.setDv(v, self.devOriginal[v]); });
        return self.devReport();
      }
      var map = { left: [-step, 0, 0, 0], right: [step, 0, 0, 0], up: [0, -step, 0, 0], down: [0, step, 0, 0],
        'w-': [0, 0, -step, 0], 'w+': [0, 0, step, 0], 'h-': [0, 0, 0, -step], 'h+': [0, 0, 0, step] };
      if (map[a]) self.devNudge(map[a]);
    });

    // Keyboard nudging (ignored while typing in the phone field)
    this.on(document, 'keydown', function (e) {
      if (!self.isOpen || document.activeElement === self.input) return;
      var k = e.key;
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].indexOf(k) < 0) return;
      e.preventDefault();
      var s = e.shiftKey ? 10 : 1;
      var d = { ArrowLeft: [-s, 0], ArrowRight: [s, 0], ArrowUp: [0, -s], ArrowDown: [0, s] }[k];
      self.devNudge(e.altKey ? [0, 0, d[0], d[1]] : [d[0], d[1], 0, 0]);
    });

    this.on(window, 'resize', function () { self.devReport(); });
    this.devSelect('field');
  };

  Popup.prototype.devSelect = function (key) {
    var self = this;
    this.devSel = key;
    Object.keys(DEV_TARGETS).forEach(function (k) { self.root.classList.remove('dtp--dev-sel-' + k); });
    this.root.classList.add('dtp--dev-sel-' + (key === 'text' ? 'field' : key));
    this.devPanel.querySelectorAll('[data-t]').forEach(function (b) {
      b.setAttribute('aria-pressed', b.getAttribute('data-t') === key ? 'true' : 'false');
    });
    this.devReport();
  };

  Popup.prototype.devNudge = function (d) {
    var t = DEV_TARGETS[this.devSel];
    var v = t.vars;
    if (this.devSel === 'text') {
      // ←/→ move where text starts; W−/W+ change the right-hand stop
      this.setDv(v[0], Math.max(0, this.dv(v[0]) + d[0]));
      this.setDv(v[1], Math.max(0, this.dv(v[1]) - d[2]));
    } else {
      this.setDv(v[0], this.dv(v[0]) + d[0]);
      this.setDv(v[1], this.dv(v[1]) + d[1]);
      this.setDv(v[2], Math.max(4, this.dv(v[2]) + d[2]));
      this.setDv(v[3], Math.max(4, this.dv(v[3]) + d[3]));
    }
    this.devReport();
  };

  Popup.prototype.devValuesText = function () {
    var self = this;
    var lines = ['/* Disco popup alignment — original-artwork pixels (' + this.dv('dtp-ref-w') + ' × ' + this.dv('dtp-ref-h') + ') */'];
    Object.keys(DEV_TARGETS).forEach(function (k) {
      var t = DEV_TARGETS[k];
      lines.push(t.vars.map(function (v) { return '--' + v + ': ' + self.dv(v) + ';'; }).join(' '));
    });
    lines.push('', 'Theme editor settings:');
    Object.keys(DEV_TARGETS).forEach(function (k) {
      var t = DEV_TARGETS[k];
      lines.push(t.label + ': ' + t.vars.map(function (v, i) { return t.settings[i] + ' = ' + self.dv(v); }).join(', '));
    });
    return lines.join('\n');
  };

  Popup.prototype.devReport = function () {
    if (!this.devPanel) return;
    var c = this.cfg;
    var t = DEV_TARGETS[this.devSel];
    var self = this;
    var stageW = this.stage.getBoundingClientRect().width;
    var scale = stageW / (this.dv('dtp-ref-w') || 1);
    var out = t.label + ':  ' + t.vars.map(function (v) { return '--' + v + ': ' + self.dv(v); }).join('  ') +
      '\n1 artwork px = ' + scale.toFixed(3) + ' screen px  ·  popup ' + Math.round(stageW) + 'px wide' +
      (this.devStep === 10 ? '  ·  step 10' : '  ·  step 1');
    var warn = '';
    if (c.imgW && c.imgH) {
      var refRatio = this.dv('dtp-ref-w') / this.dv('dtp-ref-h');
      var imgRatio = c.imgW / c.imgH;
      if (Math.abs(refRatio - imgRatio) / refRatio > 0.003) {
        warn = '\n⚠ Uploaded image is ' + c.imgW + '×' + c.imgH + ' but alignment assumes ' + this.dv('dtp-ref-w') + '×' + this.dv('dtp-ref-h') +
          '. Different shape = misaligned. Use the original framing or re-measure.';
      }
    }
    var el = this.devPanel.querySelector('[data-dev-out]');
    el.textContent = out;
    if (warn) {
      var w = document.createElement('span');
      w.className = 'dtp__dev-warn';
      w.textContent = warn;
      el.appendChild(w);
    }
  };

  Popup.prototype.devCopy = function (btn) {
    var text = this.devValuesText();
    var done = function () { btn.textContent = 'Copied!'; setTimeout(function () { btn.textContent = 'Copy values'; }, 1500); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done, function () { window.prompt('Copy these values:', text); });
    } else {
      window.prompt('Copy these values:', text);
    }
    console.info(text);
  };

  // Drag a rectangle on the artwork; it becomes the selected target's box.
  Popup.prototype.devToggleDraw = function (btn) {
    var self = this;
    if (this.devDraw) {
      this.devDraw.remove(); this.devDraw = null;
      btn.setAttribute('aria-pressed', 'false');
      return;
    }
    btn.setAttribute('aria-pressed', 'true');
    var layer = document.createElement('div');
    layer.className = 'dtp__dev-draw';
    this.stage.appendChild(layer);
    this.devDraw = layer;
    var start = null, box = null;
    var toArt = function (ev, r) {
      return {
        x: Math.max(0, Math.min(r.width, ev.clientX - r.left)) / r.width * self.dv('dtp-ref-w'),
        y: Math.max(0, Math.min(r.height, ev.clientY - r.top)) / r.height * self.dv('dtp-ref-h')
      };
    };
    layer.addEventListener('pointerdown', function (ev) {
      ev.preventDefault();
      layer.setPointerCapture(ev.pointerId);
      var r = self.stage.getBoundingClientRect();
      start = { p: toArt(ev, r), r: r };
      if (box) box.remove();
      box = document.createElement('div');
      box.className = 'dtp__dev-drawbox';
      self.stage.appendChild(box);
    });
    layer.addEventListener('pointermove', function (ev) {
      if (!start) return;
      var p = toArt(ev, start.r);
      var L = Math.min(p.x, start.p.x), T = Math.min(p.y, start.p.y);
      var W = Math.abs(p.x - start.p.x), H = Math.abs(p.y - start.p.y);
      var rw = self.dv('dtp-ref-w'), rh = self.dv('dtp-ref-h');
      box.style.left = (L / rw * 100) + '%'; box.style.top = (T / rh * 100) + '%';
      box.style.width = (W / rw * 100) + '%'; box.style.height = (H / rh * 100) + '%';
      box.dataset.v = [Math.round(L), Math.round(T), Math.round(W), Math.round(H)].join(',');
    });
    layer.addEventListener('pointerup', function () {
      start = null;
      if (!box || !box.dataset.v || self.devSel === 'text') return;
      var v = box.dataset.v.split(',').map(Number);
      if (v[2] < 4 || v[3] < 4) return;
      var vars = DEV_TARGETS[self.devSel].vars;
      for (var i = 0; i < 4; i++) self.setDv(vars[i], v[i]);
      self.devReport();
    });
  };

  Popup.prototype.destroy = function () {
    this.timers.forEach(clearTimeout);
    this.cleanups.forEach(function (fn) { try { fn(); } catch (e) {} });
    if (this.devDraw) this.devDraw.remove();
    document.documentElement.classList.remove('dtp-lock');
    if (this.root && this.root.parentNode) this.root.parentNode.removeChild(this.root);
  };

  /* ======================================================================
     Boot + theme editor support
     ====================================================================== */
  function boot(scope) {
    (scope || document).querySelectorAll('[data-dtp]').forEach(function (root) {
      var id = root.getAttribute('data-section-id');
      if (instances[id] && instances[id].root === root) return;
      if (instances[id]) instances[id].destroy();
      instances[id] = new Popup(root);
    });
  }

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var id = e.detail && e.detail.sectionId;
    if (id && instances[id]) { instances[id].destroy(); delete instances[id]; }
  });
  document.addEventListener('shopify:section:select', function (e) {
    var id = e.detail && e.detail.sectionId;
    if (id && instances[id]) instances[id].open(false);
  });

  window.DiscoTribePopup = {
    open: function () { Object.keys(instances).forEach(function (k) { instances[k].open(false); }); },
    close: function () { Object.keys(instances).forEach(function (k) { instances[k].close('api'); }); },
    reset: function () {
      [KEY.subscribed, KEY.dismissedUntil].forEach(function (k) { del(local, k); });
      [KEY.shown, KEY.pageviews].forEach(function (k) { del(session, k); });
      return 'Disco popup memory cleared on this device.';
    },
    _normalizePhone: normalizePhone
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
})();
